/**
 * /admin/edit — search browser to reach the RegistryFieldEditor for
 * any degree or university. Solves the "how do I edit a course
 * that never had a family flag" gap.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, GraduationCap, Building2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

export default function AdminEditBrowser() {
  const { user, loading: authLoading } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const runSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get(
        `/admin/course-search?q=${encodeURIComponent(query.trim())}&limit=15`,
      );
      setResults(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 250);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  if (authLoading) {
    return <div className="p-6 text-stone-400 text-sm"><Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Loading…</div>;
  }
  if (!user) {
    return <div className="p-6 text-stone-600">Sign in as admin to view this page.</div>;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-900"
          data-testid="admin-edit-back"
        >
          <ArrowLeft className="h-3 w-3" /> Admin home
        </Link>
        <h1 className="mt-4 font-editorial text-4xl sm:text-5xl text-stone-900">
          Edit any course or university
        </h1>
        <p className="mt-2 text-stone-600 text-sm max-w-2xl">
          Search by course title, university name, or id. Every save
          writes an audit row with a Staff chip visible only to ops
          — families see a "Sourced" chip only when you include a
          source URL.
        </p>

        <div className="mt-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Oxford medicine, uk-manchester, Berkeley..."
            className="pl-9 h-11"
            data-testid="admin-edit-search-input"
            autoFocus
          />
        </div>

        <div className="mt-6">
          {loading && (
            <div className="text-[12px] text-stone-500">
              <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Searching…
            </div>
          )}
          {err && <div className="text-sm text-rose-700">Error: {err}</div>}

          {results && !loading && (
            <>
              {results.degrees.length > 0 && (
                <div className="mb-6">
                  <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2">
                    Courses ({results.degrees_count})
                  </div>
                  <ul
                    className="border border-stone-200 rounded-xl bg-white divide-y divide-stone-100"
                    data-testid="admin-edit-degrees-list"
                  >
                    {results.degrees.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50"
                        data-testid={`admin-edit-degree-row-${d.id}`}
                      >
                        <GraduationCap className="h-4 w-4 text-stone-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-stone-900 truncate">
                            {d.official_title || d.id}
                          </div>
                          <div className="text-[11px] text-stone-500 truncate font-mono">
                            {d.university_name || d.university_id}
                            {d.qualification && ` · ${d.qualification}`}
                            {d.campus && ` · ${d.campus}`}
                          </div>
                        </div>
                        <Link
                          to={`/admin/edit/degree/${d.id}`}
                          className="text-[11px] font-underground-sc tracking-wider text-white bg-purple-800 hover:bg-purple-900 rounded px-3 py-1.5 shrink-0"
                          data-testid={`admin-edit-degree-open-${d.id}`}
                        >
                          Edit fields →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results.universities.length > 0 && (
                <div>
                  <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2">
                    Universities ({results.unis_count})
                  </div>
                  <ul
                    className="border border-stone-200 rounded-xl bg-white divide-y divide-stone-100"
                    data-testid="admin-edit-unis-list"
                  >
                    {results.universities.map((u) => (
                      <li
                        key={u.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50"
                        data-testid={`admin-edit-uni-row-${u.id}`}
                      >
                        <Building2 className="h-4 w-4 text-stone-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-stone-900 truncate flex items-center gap-2">
                            {u.name || u.id}
                            {u.claimed && (
                              <span className="text-[9px] font-underground-sc tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1 py-0.5">
                                claimed
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-stone-500 truncate font-mono">
                            {u.city || "—"}
                            {u.country && `, ${u.country}`}
                          </div>
                        </div>
                        <Link
                          to={`/admin/edit/university/${u.id}`}
                          className="text-[11px] font-underground-sc tracking-wider text-white bg-purple-800 hover:bg-purple-900 rounded px-3 py-1.5 shrink-0"
                          data-testid={`admin-edit-uni-open-${u.id}`}
                        >
                          Edit fields →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results.degrees.length === 0 && results.universities.length === 0 && (
                <div
                  className="text-sm text-stone-500 italic"
                  data-testid="admin-edit-empty"
                >
                  Nothing matching "{results.query}". Try a shorter phrase.
                </div>
              )}
            </>
          )}

          {!results && !loading && q.trim().length < 2 && (
            <div className="text-[12px] text-stone-500">
              Type at least 2 characters to search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
